import { APP_INITIALIZER, ApplicationConfig } from '@angular/core';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';

import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import { LoaderInterceptor } from './shared/interceptor/loader-interceptor';
import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';

import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { APP_ADMIN_ROUTES } from './app-admin.routes';
import { APP_ROUTES } from './app.routes';
import { UsageLimitService } from './shared/services/usage-limit/usage-limit.service';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { provideI18n } from './core/i18n/transloco.config';
import { APP_LANG_STORAGE_KEY } from './core/i18n/app-lang.constants';
import { preloadAppLang } from './core/i18n/preload-lang-scopes';
import { TranslocoService } from '@jsverse/transloco';
import { createHubAuthEpochInitializer } from './shared/utils/hub-auth-epoch';
import {
    isRedeAdsConnectProfilePath,
    REDE_ADS_INTEGRATION_LINK_TOKEN_STORAGE_KEY,
} from './shared/constants/rede-ads-connect.constants';
import { HubStorageKey, StorageService, TokenStorageKey } from './shared/services/storage/storage.service';
import { readHubTokenFromUrl } from './shared/utils/hub-product-token';

registerLocaleData(localePt, 'pt-BR');
registerLocaleData(localeEn, 'en-US');
registerLocaleData(localeEs, 'es-ES');

/**
 * ?hubToken= do hub-app-front vira accessToken da sessão.
 * Nas rotas Rede Ads, ?token= é JWT de integração, não sessão do Hub.
 * A flag de bootstrap evita HTTP precoce em initializeApp (401 → logout).
 */
export function applyTokenFromQuery(storage: StorageService) {
    return () => {
        if (typeof window === 'undefined') return Promise.resolve();

        const params = new URLSearchParams(window.location.search);
        if (isRedeAdsConnectProfilePath(window.location.pathname)) {
            const integrationToken = params.get('token')?.trim();
            if (integrationToken) {
                sessionStorage.setItem(REDE_ADS_INTEGRATION_LINK_TOKEN_STORAGE_KEY, integrationToken);
            }
            return Promise.resolve();
        }

        const hubToken = readHubTokenFromUrl(window.location.search);
        if (hubToken) {
            storage.setLocal(TokenStorageKey.ACCESS_TOKEN, hubToken);
            storage.setSession(TokenStorageKey.FROM_QUERY, '1');
            storage.setSession(HubStorageKey.BOOTSTRAP_AT, String(Date.now()));
        }
        return Promise.resolve();
    };
}

const routes = [
    ...APP_ROUTES,
    ...APP_ADMIN_ROUTES,
    {
        path: '**',
        redirectTo: 'v2',
    },
];

const AVAILABLE_LANGS = ['pt-BR', 'en-US', 'es-ES'] as const;

/** Idioma salvo no localStorage; usado para não pré-carregar en-US/es-ES sem necessidade. */
function resolveInitialLang(): (typeof AVAILABLE_LANGS)[number] {
    if (typeof localStorage === 'undefined') {
        return 'pt-BR';
    }

    const stored = localStorage.getItem(APP_LANG_STORAGE_KEY);
    return AVAILABLE_LANGS.includes(stored as (typeof AVAILABLE_LANGS)[number])
        ? (stored as (typeof AVAILABLE_LANGS)[number])
        : 'pt-BR';
}

/**
 * Pré-carrega o idioma ativo na inicialização (root + escopos do shell v2 e painel padrão).
 * Outros escopos entram sob demanda ao navegar/trocar idioma.
 */
export function preloadSharedScope(transloco: TranslocoService) {
    return () => {
        const lang = resolveInitialLang();

        return firstValueFrom(preloadAppLang(transloco, lang));
    };
}

export function initializeApp(usageLimitService: UsageLimitService, storage: StorageService) {
    return () => {
        const accessToken = storage.getLocal(TokenStorageKey.ACCESS_TOKEN);
        const tokenFromQuery = storage.getSession(TokenStorageKey.FROM_QUERY);
        if (tokenFromQuery === '1') {
            // Token veio do Hub (?hubToken=)
            return Promise.resolve(true);
        }
        if (accessToken) {
            const usage$ = usageLimitService.refreshUsage().pipe(catchError(() => of(null)));
            return firstValueFrom(forkJoin([usage$]));
        }
        return Promise.resolve(true);
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideCharts(withDefaultRegisterables()),
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: applyTokenFromQuery,
            deps: [StorageService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: createHubAuthEpochInitializer,
            deps: [StorageService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [UsageLimitService, StorageService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: preloadSharedScope,
            deps: [TranslocoService],
            multi: true,
        },
        provideRouter(routes, withPreloading(PreloadAllModules)),
        provideClientHydration(),
        provideAnimations(),
        provideHttpClient(withFetch(), withInterceptorsFromDi()),
        provideI18n(),
        provideToastr(),
        { provide: LOCALE_ID, useValue: 'pt-BR' },
    ],
};
